import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bell, CheckCircle2, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, fmtDate } from "@/components/Bits";

const Notifications = () => {
  const [n, setN] = useState(null);
  useEffect(() => { api.get("/admin/notifications").then((r) => setN(r.data)).catch(() => {}); }, []);
  if (!n || n.items.length === 0) return null;
  const markRead = async () => {
    await api.post("/admin/notifications/read");
    setN({ items: n.items.map((x) => ({ ...x, read: true })), unread: 0 });
    window.dispatchEvent(new Event("admin-notifications-read"));
  };
  return (
    <div className="mb-8 rounded-2xl border border-border bg-card p-6 max-w-5xl" data-testid="admin-notifications">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg flex items-center gap-2"><Bell className="h-4 w-4 text-pinkb" />Notifications {n.unread > 0 && <span className="text-xs rounded-full bg-pinkb text-white px-2 py-0.5">{n.unread} new</span>}</h3>
        {n.unread > 0 && <button onClick={markRead} data-testid="notifications-mark-read" className="text-xs text-pinkb hover:underline">Mark all as read</button>}
      </div>
      <ul className="space-y-2 text-sm">
        {n.items.map((x) => (
          <li key={x.id} data-testid={`notification-${x.id}`} className={`rounded-lg px-3 py-2 ${x.read ? "bg-secondary/50 text-muted-foreground" : "bg-secondary"}`}>{x.text} <span className="text-xs text-muted-foreground ml-1">{fmtDate(x.created_at)}</span></li>
        ))}
      </ul>
    </div>
  );
};

const GapFlags = ({ flags, onResolve }) => (
  <div className="mb-8 rounded-2xl border-2 border-rose-200 bg-rose-50 p-6 max-w-5xl" data-testid="kb-gap-flags">
    <h3 className="text-lg flex items-center gap-2 text-rose-700"><AlertTriangle className="h-4 w-4" />Knowledge gaps to fix</h3>
    <p className="text-xs text-muted-foreground mt-1 mb-4">Sections flagged after 3 or more "not helpful" ratings in 30 days. Rewrite them in the <Link to="/admin/kb" className="text-pinkb hover:underline">knowledge bank</Link>, then mark them fixed.</p>
    {flags.length === 0 && <p className="text-sm text-muted-foreground" data-testid="kb-gap-empty">No gaps flagged. Nice work.</p>}
    <ul className="space-y-2">
      {flags.map((f) => (
        <li key={f.id} data-testid={`kb-gap-${f.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white px-4 py-3 text-sm">
          <div><div className="font-medium">{f.title}</div><div className="text-xs text-muted-foreground">{f.count} not-helpful ratings · flagged {fmtDate(f.created_at)}</div></div>
          <button onClick={() => onResolve(f)} data-testid={`kb-gap-resolve-${f.id}`} className="flex items-center gap-1 text-xs rounded-full border border-border px-3 py-1.5 hover:border-pinkb hover:text-pinkb transition-colors"><CheckCircle2 className="h-3.5 w-3.5" />Mark fixed</button>
        </li>
      ))}
    </ul>
  </div>
);

const GapItem = ({ g, onChange }) => {
  const [busy, setBusy] = useState(false);
  const draft = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/kb-gaps/${g.id}/draft`);
      onChange({ ...g, status: "drafted" });
      toast.success("Draft added to the knowledge bank for you to review");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dismiss = async () => {
    try { await api.post(`/admin/kb-gaps/${g.id}/dismiss`); onChange(null); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li data-testid={`ai-gap-${g.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white px-4 py-3 text-sm">
      <div className="min-w-0 flex-1"><div className="font-medium break-words">{g.question}</div><div className="text-xs text-muted-foreground">{g.user_name} · {fmtDate(g.created_at)}</div></div>
      <div className="flex gap-2">
        {g.status === "drafted"
          ? <Link to="/admin/kb" data-testid={`ai-gap-view-draft-${g.id}`} className="text-xs rounded-full bg-amber-100 text-amber-800 px-3 py-1.5">Draft ready, review it</Link>
          : <button onClick={draft} disabled={busy} data-testid={`ai-gap-draft-${g.id}`} className="flex items-center gap-1 text-xs rounded-full bg-navy text-white px-3 py-1.5 hover:bg-navy/90 transition-colors disabled:opacity-60">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}{busy ? "Drafting…" : "Draft an entry"}</button>}
        <button onClick={dismiss} data-testid={`ai-gap-dismiss-${g.id}`} className="p-1.5 text-muted-foreground hover:text-rose-600" aria-label="Dismiss"><X className="h-4 w-4" /></button>
      </div>
    </li>
  );
};

const AiGaps = () => {
  const [gaps, setGaps] = useState([]);
  useEffect(() => { api.get("/admin/kb-gaps").then((r) => setGaps(r.data)).catch(() => {}); }, []);
  const change = (id) => (next) => setGaps((l) => (next ? l.map((x) => (x.id === id ? next : x)) : l.filter((x) => x.id !== id)));
  return (
    <div className="mb-8 rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 max-w-5xl" data-testid="ai-gaps">
      <h3 className="text-lg flex items-center gap-2"><Sparkles className="h-4 w-4 text-pinkb" />Questions the AI stretched to answer</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-4">Members got a live answer from the closest knowledge bank content. Draft a proper entry so next time it's spot on. Drafts stay hidden from the AI until you publish them.</p>
      {gaps.length === 0 && <p className="text-sm text-muted-foreground" data-testid="ai-gaps-empty">Nothing logged. The knowledge bank is covering everything so far.</p>}
      <ul className="space-y-2">{gaps.map((g) => <GapItem key={g.id} g={g} onChange={change(g.id)} />)}</ul>
    </div>
  );
};

const SectionsTable = ({ sections }) => (
  <div className="rounded-2xl border border-border bg-card p-6 max-w-5xl overflow-x-auto" data-testid="answer-ratings-report">
    <h3 className="text-lg mb-1">Answers by knowledge bank section</h3>
    <p className="text-xs text-muted-foreground mb-4">"Used in answers" shows which sections are referenced most, so you know where updates matter. Sections at the top get the most "not helpful" ratings.</p>
    <div className="flex flex-wrap gap-2 mb-4 text-xs" data-testid="most-referenced">{[...sections].sort((a, b) => b.references - a.references).slice(0, 5).filter((x) => x.references).map((x) => <span key={x.title} className="rounded-full bg-secondary px-3 py-1">{x.title} · {x.references}</span>)}</div>
    <table className="w-full text-sm">
      <thead className="text-[11px] font-mono uppercase text-muted-foreground"><tr className="border-b border-border"><th className="text-left py-2">Section</th><th className="text-right py-2">Used in answers</th><th className="text-right py-2">Helpful</th><th className="text-right py-2">Not helpful</th><th className="text-right py-2">Score</th></tr></thead>
      <tbody>
        {sections.map((x) => (
          <tr key={x.title} className="border-b border-border last:border-0" data-testid="ratings-row">
            <td className="py-2 pr-4">{x.title}{x.flagged && <span className="ml-2 text-[10px] font-mono uppercase rounded-full bg-rose-100 text-rose-700 px-2 py-0.5" data-testid="ratings-row-flagged">Gap</span>}</td>
            <td className="py-2 text-right font-mono" data-testid="ratings-row-refs">{x.references}</td><td className="py-2 text-right text-emerald-700">{x.helpful}</td><td className="py-2 text-right text-rose-600">{x.not_helpful}</td>
            <td className="py-2 text-right font-mono">{x.score ?? "–"}%</td>
          </tr>
        ))}
        {sections.length === 0 && <tr><td colSpan={5} className="py-3 text-muted-foreground">No rated answers yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

const Unhelpful = ({ items }) => items.length > 0 && (
  <div className="mt-8 rounded-2xl border border-border bg-card p-6 max-w-5xl" data-testid="recent-unhelpful">
    <h3 className="text-lg mb-3">Recent answers marked not helpful</h3>
    <ul className="space-y-2 text-sm">{items.map((q) => <li key={q.id} className="rounded-lg bg-secondary px-3 py-2">{q.question}<div className="text-xs text-muted-foreground mt-1">Sections used: {q.kb_sources.join(", ") || "—"}</div></li>)}</ul>
  </div>
);

export default function AdminReports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/answer-ratings").then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const resolve = async (f) => {
    try {
      await api.post(`/admin/kb-flags/${f.id}/resolve`);
      setR({ ...r, flags: r.flags.filter((x) => x.id !== f.id), sections: r.sections.map((s) => (s.title === f.title ? { ...s, flagged: false } : s)) });
      toast.success("Marked as fixed");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow={r ? `${r.rated} rated answers · ${r.rated ? Math.round((r.helpful * 100) / r.rated) : 0}% helpful` : "Reports"} title="What's landing with members." />
      <Notifications />
      <AiGaps />
      {r && <GapFlags flags={r.flags} onResolve={resolve} />}
      {r && <SectionsTable sections={r.sections} />}
      {r && <Unhelpful items={r.recent_unhelpful} />}
    </div>
  );
}
